import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getFilterWeightageKey } from 'src/features/Icp/utils';
import { RealTimeICPConfigData } from './types';
import { getEmpByDeptValues, getICPSetUpStatus, getIntentTopics, getRealTimeICPConfig } from './icp.actions';

export type InitialState = {
  filters: any[];
  realTimeICPConfig: Record<string, RealTimeICPConfigData[]>;
  getLoader: boolean;
  empByDeptValues: string[];
  intentTopicsValues: string[];
  isRealTimeICPConfigured: boolean;
};

const initialState: InitialState = {
  filters: [],
  realTimeICPConfig: {},
  getLoader: false,
  empByDeptValues: [],
  intentTopicsValues: [],
  isRealTimeICPConfigured: false,
};

const icpScoringSlice = createSlice({
  name: 'icp-scoring',
  initialState,
  reducers: {
    setFilters: (state, { payload }: PayloadAction<any>) => {
      const updatedFilters = (Array.isArray(payload) ? payload : []).map((item: any) => {
        const keyName = getFilterWeightageKey(item.filterKey);
        const includes = Array.isArray(item?.includes) ? item.includes : [];
        const excludes = Array.isArray(item?.excludes) ? item.excludes : [];
        const excludeList = excludes.map((cat: any) => {
          const match = item.excludeList?.find((entry: any) => cat in entry);
          return match || { [cat]: 2, type: '' };
        });
        let includeList = includes.map((cat: any) => {
          const match = item?.includeList?.find((entry: any) => cat in entry);
          return match || { [cat]: 2, type: '' };
        });
        if (item?.range?.length > 0) {
          includeList = item.range.map((range: any) => {
            const min = range.min;
            const max = range.max;
            const key = `${min != null ? min : '+'}${min != null && max != null ? '-' : ''}${
              max != null ? max : '+'
            }`;

            const match = item?.includeList?.find((entry: any) => key in entry);
            return match || { [key]: 2, type: '' };
          });
        }
        if( item?.fromDate && item?.toDate ){
          includeList = item?.includeList || [{[item?.fromDate + 'to' + item?.toDate] : 2, type: ''}]
        }
        if(item.filterKey === 'employeeByDepartment'){
          includeList = item?.includeList || []
        }
        if (item.filterKey === 'employeeDistribution' || item.filterKey === 'Global Presence') {
          const firstInclude = item?.includes?.[0];
          let globalPresence = firstInclude || {};
          if (typeof firstInclude === 'string') {
            try {
              globalPresence = JSON.parse(firstInclude || '{}');
            } catch (error) {
              globalPresence = {};
            }
          }
          const globalPresenceType =
            globalPresence?.mode === 'SPECIFIC_COUNTRIES'
              ? 'Specific Countries'
              : 'At Least N Countries';
          includeList = [
            {
              globalPresenceType,
              ...(globalPresenceType === 'Specific Countries'
                ? {
                    'Select Countries': globalPresence?.countries || [],
                  }
                : {
                    'Number of countries': globalPresence?.country_count || 0,
                  }),
              'Minimum Employees per Country': globalPresence?.employee_per_country || 0,
              weight: item?.includeList?.[0]?.weight || 2,
              type: item?.includeList?.[0]?.type || '',
            },
          ];
        }
        return {
          filterKey: item.filterKey,
          excludeList,
          includeList,
          primaryKey: item.primaryKey,
          ...(keyName && {
            [keyName]: item[keyName] || 2,
          }),
        };
      });
      state.filters = updatedFilters;
    },
    setWeightageForFilters: (state, { payload }: PayloadAction<any>) => {
      state.filters = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRealTimeICPConfig.pending, (state) => {
      state.getLoader = true;
      state.realTimeICPConfig = {};
    });
    builder.addCase(getRealTimeICPConfig.fulfilled, (state, action) => {
      state.getLoader = false;
      state.realTimeICPConfig = action.payload;
    });
    builder.addCase(getRealTimeICPConfig.rejected, (state) => {
      state.getLoader = false;
      state.realTimeICPConfig = {};
    });
    builder.addCase(getEmpByDeptValues.pending, (state) => {
      state.getLoader = true;
      state.empByDeptValues = [];
    });
    builder.addCase(getEmpByDeptValues.fulfilled, (state, action) => {
      state.getLoader = false;
      state.empByDeptValues = action.payload;
    });
    builder.addCase(getEmpByDeptValues.rejected, (state) => {
      state.getLoader = false;
      state.empByDeptValues = [];
    });
    builder.addCase(getICPSetUpStatus.pending, (state) => {
      state.isRealTimeICPConfigured = false;
    });
    builder.addCase(getICPSetUpStatus.fulfilled, (state, action) => {
      state.isRealTimeICPConfigured = action.payload.sproutsRecommendedIcpPresent;
    });
    builder.addCase(getICPSetUpStatus.rejected, (state) => {
      state.isRealTimeICPConfigured = false;
    });
    builder.addCase(getIntentTopics.pending, (state) => {
      state.getLoader = true;
      state.intentTopicsValues = [];
    });
    builder.addCase(getIntentTopics.fulfilled, (state, action) => {
      state.getLoader = false;
      state.intentTopicsValues = action.payload;
    });
    builder.addCase(getIntentTopics.rejected, (state) => {
      state.getLoader = false;
      state.intentTopicsValues = [];
    });
  }
});

export const { setFilters, setWeightageForFilters } = icpScoringSlice.actions;

export default icpScoringSlice.reducer;
